import React, {useEffect, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import useAuth from "src/hooks/useAuth";
import useSettings from "src/hooks/useSettings";
import {HOSTS_CONFIG} from "src/constants";
import {updateLanguagePreference} from "src/api/profile";
import _ from "lodash";

const LanguageSelectionDialog = () => {
    const {user, isAuthenticated, updateUser} = useAuth();
    const {saveSettings} = useSettings();
    const {t, i18n} = useTranslation("notifications");
    const hostname = window.location.hostname;
    const hostConfig = _.get(HOSTS_CONFIG, `${hostname}`);
    const supportedLanguages = (hostConfig && hostConfig.supportedLanguages) || [];

    const initialLanguage = supportedLanguages.includes(i18n.language)
        ? i18n.language
        : (hostConfig && hostConfig.defaultLanguage) || supportedLanguages[0];

    const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
    const [open, setOpen] = useState(true);
    const [saving, setSaving] = useState(false);

    const shouldShow = isAuthenticated && user && !user.language && open && supportedLanguages.length > 0;
    const isSingleLanguage = shouldShow && supportedLanguages.length === 1;

    useEffect(() => {
        if (isSingleLanguage) {
            const language = supportedLanguages[0];
            updateLanguagePreference(language)
                .then(() => {
                    saveSettings({language});
                    updateUser({language});
                    setOpen(false);
                })
                .catch(console.error);
        }
    }, [isSingleLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!shouldShow || isSingleLanguage) {
        return null;
    }

    const handleSave = async (language) => {
        setSaving(true);
        try {
            await updateLanguagePreference(language);
            saveSettings({language});
            updateUser({language});
            setOpen(false);
        } catch (error) {
            console.error(error);
            setSaving(false);
        }
    };

    return (
        <Dialog open disableBackdropClick disableEscapeKeyDown>
            <DialogTitle disableTypography>
                <Typography variant="h4">{t("languageSelection.title")}</Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Typography gutterBottom>{t("languageSelection.text")}</Typography>
                <TextField
                    fullWidth
                    label={t("languageSelection.label")}
                    name="language"
                    onChange={event => setSelectedLanguage(event.target.value)}
                    select
                    SelectProps={{native: true}}
                    value={selectedLanguage}
                    variant="outlined"
                    style={{marginTop: 16}}
                >
                    {supportedLanguages.map(language => (
                        <option key={language} value={language}>
                            {t(`languageSelection.languageSelect.${language}`)}
                        </option>
                    ))}
                </TextField>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="secondary" onClick={() => handleSave(selectedLanguage)} disabled={saving}>
                    {t("languageSelection.saveCta")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LanguageSelectionDialog;
